import { IGroup, IGroupValue } from "@modules/vacancies/contextVacancies/ContextVacancies";
import { useEffect, useMemo, useState } from "react";

interface useGroupsInputStateProps {
  groups: IGroup[];
  linkId: string;
  isEditing?: boolean;
  onChange?: (values: IGroupValue[]) => void;
  onChangeRetaker?: (values: IGroupValue[]) => void;
}

/**
 * Custom hook to manage the state of group inputs.
 *
 * @param {useGroupsInputStateProps} [props] - Optional properties to initialize the hook.
 * @param {IGroup[]} [props.groups=[]] - Array of group objects.
 * @param {string} [props.linkId] - Identifier for the link.
 *
 * @returns {Object} - Returns an object containing the current state of group vacancies and a function to handle input changes.
 * @returns {IGroupValue[]} countVacancies - The current state of group vacancies.
 * @returns {Function} onChangeInput - Function to handle input changes.
 *
 * @function onChangeInput
 * @param {any} value - The new value for the input.
 * @param {string} groupId - The ID of the group to update.
 *
 * @function setInitialState
 * Initializes the state with the initial group vacancies.
 *
 * @example
 * const { countVacancies, onChangeInput } = useGroupsInputState({ groups, linkId });
 *
 * // To handle input change
 * onChangeInput(newValue, groupId);
 */
const useGroupsInputState = (props?: useGroupsInputStateProps) => {
  const { groups = [], linkId, onChange, onChangeRetaker, isEditing } = props;
  const [countVacancies, setCountVacancies] = useState(null);
  const [retakerCapacities, setRetakerCapacities] = useState<IGroupValue[] | null>(null);
  const [newReferentGroupId, setNewReferentGroupId] = useState<string | null>(null);

  const referentGroup = useMemo(() => {
    if (newReferentGroupId !== null) {
      return newReferentGroupId;
    }

    return groups.find((group: IGroup) => group.isReferent)?.id ?? null;
  }, [newReferentGroupId, groups]);

  const onChangeInput = (value: any, groupId: string) => {
    const newValue = value ? parseInt(value) : 0;
    setCountVacancies((prevState: IGroupValue[]) => {
      const updatedVacancies = [...prevState];
      const indexGroup = updatedVacancies.findIndex(
        (group: IGroupValue) => group.group.id === groupId,
      );

      /**
       * @note Should never happen — groupId always originates from the same groups array that initialized this state.
       * Guard added for robustness only.
       */
      if (indexGroup === -1) {
        return prevState;
      }

      updatedVacancies[indexGroup] = {
        ...updatedVacancies[indexGroup],
        value: newValue,
      };

      onChange?.(updatedVacancies);

      return updatedVacancies;
    });
  };

  const onChangeRetakerInput = (value: any, groupId: string) => {
    const newValue = value !== "" && value !== null && value !== undefined ? parseInt(value) : null;
    setRetakerCapacities((prevState: IGroupValue[]) => {
      const updated = [...prevState];
      const index = updated.findIndex((item: IGroupValue) => item.group.id === groupId);

      /**
       * @note Should never happen — groupId always originates from the same groups array that initialized this state.
       * Guard added for robustness only.
       */
      if (index === -1) {
        return prevState;
      }

      updated[index] = { ...updated[index], value: newValue };
      onChangeRetaker?.(updated);

      return updated;
    });
  };

  const setInitialState = () => {
    const countGroupVacancies = groups.map((group: IGroup) => ({
      linkId,
      group,
      value: group.vacancies,
    }));

    const initialRetakerCapacities = groups.map((group: IGroup) => ({
      linkId,
      group,
      value: group.groupCapacity?.retakerCapacity ?? null,
    }));

    setCountVacancies(countGroupVacancies);
    setRetakerCapacities(initialRetakerCapacities);
    onChange?.(countGroupVacancies);
    onChangeRetaker?.(initialRetakerCapacities);
  };

  useEffect(() => {
    if (!countVacancies || countVacancies?.length !== (groups ?? [])?.length) {
      setInitialState();
    }
  }, [groups]);

  useEffect(() => {
    setNewReferentGroupId(null);

    if (!isEditing) {
      setInitialState();
    }
  }, [isEditing]);

  return {
    countVacancies,
    onChangeInput,
    retakerCapacities,
    onChangeRetakerInput,
    referentGroup,
    setNewReferentGroupId,
  };
};

export default useGroupsInputState;
